import * as yup from 'yup';
import { getMessage } from '@tcc/i18n';
import { formatNumberGrouped } from 'util/formatters/numberFormatter';
import { currencyFormatter } from 'util/locale';
import { GetSchemaOptions } from '../types';
import { getAvgFixedPopulationRateMax, getAvgFixedPopulationRateMin } from '../utils';

export const getFieldsSchema = (validateFieldsProperties: boolean, options: GetSchemaOptions) => {
  return {
    fields: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        name: yup.string().required(),
        hybrid:
              validateFieldsProperties
                ? yup.object({
                  id: yup.string().required(getMessage('help-text__value-required', { prop: getMessage('hybrid') })),
                  name: yup.string().required(),
                  brand: yup.string().required(),
                })
                : yup.object().nullable(),
        avgFixedPopulation:
              validateFieldsProperties
                ? getAvgFixedPopulationInputSchema(options)
                : yup.number().nullable(),
        seedCostPerBag:
              validateFieldsProperties
                ? getSeedCostInputSchema(options)
                : yup.number().nullable(),
        targetYield:
              validateFieldsProperties
                ? getTargetYieldInputSchema(options)
                : yup.number().nullable(),
        recommendation: yup.object().nullable(),
        selectedRecommendation: validateFieldsProperties
          ? yup.string().required(getMessage('help-text__generate-recommendation-required'))
          : yup.string().nullable(),
      })
    )
    .min(1, getMessage('bulk-fvss-error-no-fields-selected__step_1'))
    .required(getMessage('bulk-fvss-error-no-fields-selected__step_1')
    ),
  };
};

export const getAvgFixedPopulationInputSchema = (options: GetSchemaOptions) => {
  const { productValidationValues } = options;
  const { RATE } = productValidationValues?.PLANTING?.CORN || {};
  const avgFixedPopulationRateMin = getAvgFixedPopulationRateMin(RATE);
  const avgFixedPopulationRateMax = getAvgFixedPopulationRateMax(RATE);
  return  yup
  .number()
  .typeError(getMessage('help-text__value-num'))
  .integer(getMessage('value-needs-to-be-integer'))
  .min(avgFixedPopulationRateMin, getMessage('help-text__value-min', {
    prop: getMessage('seeding-rate'),
    min: formatNumberGrouped(avgFixedPopulationRateMin)
  }))
  .max(avgFixedPopulationRateMax, getMessage('help-text__value-max', {
    prop: getMessage('seeding-rate'),
    max: formatNumberGrouped(avgFixedPopulationRateMax)
  }))
  .required(getMessage('help-text__value-required', { prop: getMessage('seeding-rate') }));
};

export const getSeedCostInputSchema = (options: GetSchemaOptions) => {
  const { productValidationValues, currency } = options;
  const { SEED_COST } = productValidationValues?.PLANTING?.CORN || {};
  return yup.number()
  .typeError(getMessage('help-text__value-num', { prop: getMessage('seed-cost') }))
  .min(SEED_COST.min, getMessage('help-text__value-min', {
    prop: getMessage('seed-cost'),
    min: currencyFormatter(formatNumberGrouped(SEED_COST.min), currency)
  }))
  .max(SEED_COST.max, getMessage('help-text__value-max', {
    prop: getMessage('seed-cost'),
    max: currencyFormatter(formatNumberGrouped(SEED_COST.max), currency)
  }))
  .required(getMessage('help-text__value-required', { prop: getMessage('seed-cost') }));
};

export const getTargetYieldInputSchema = (options: GetSchemaOptions) => {
  const { productValidationValues } = options;
  const { TARGET_YIELD } = productValidationValues?.PLANTING?.CORN || {};
  return yup
  .number()
  .typeError(getMessage('help-text__value-num', { prop: getMessage('target-yield') }))
  .min(TARGET_YIELD.min, getMessage('help-text__value-min', {
    prop: getMessage('target-yield'),
    min: formatNumberGrouped(TARGET_YIELD.min)
  }))
  .max(TARGET_YIELD.max, getMessage('help-text__value-max', {
    prop: getMessage('target-yield'),
    max: formatNumberGrouped(TARGET_YIELD.max)
  }))
  .required(getMessage('help-text__value-required', { prop: getMessage('target-yield') }));
};
